'use client';

import { Portal } from '@common/components';
import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import { throttle } from 'es-toolkit';
import { AnimatePresence, motion, useAnimation } from 'framer-motion';
import { useEffect, useState } from 'react';

import { Image } from '@/components/client';
import { ROUTES, SELECTOR } from '@/constants';

export const NextContentsFloatingBanner = () => {
  const [isShow, setIsShow] = useState(false);
  const [isGrowUp, setIsGrowUp] = useState(false);
  const [mainElement, setMainElement] = useState<HTMLElement>();
  const controls = useAnimation();

  useEffect(() => {
    const mainElement = document.getElementById(SELECTOR.MAIN);
    if (mainElement) {
      setMainElement(mainElement);
    }
  }, []);

  useEffect(() => {
    if (mainElement === undefined) return;

    const handleScroll = throttle(() => {
      const isNearBottom =
        mainElement.scrollHeight -
          mainElement.scrollTop -
          mainElement.clientHeight <=
        150;

      const scrollPercentage =
        (mainElement.scrollTop /
          (mainElement.scrollHeight - mainElement.clientHeight)) *
        100;
      const isScrolledHalfway = scrollPercentage >= 50;

      if (isScrolledHalfway) {
        setIsGrowUp(isNearBottom);
      }

      setIsShow(isNearBottom || isScrolledHalfway);
    }, 200);

    const mutationObserver = new MutationObserver(() => {
      handleScroll();
    });

    mutationObserver.observe(mainElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    mainElement.addEventListener('scroll', handleScroll);

    return () => {
      mainElement.removeEventListener('scroll', handleScroll);
      mutationObserver.disconnect();
    };
  }, [mainElement]);

  useEffect(() => {
    controls.start({
      opacity: isShow ? 1 : 0,
      y: isShow ? 0 : 10,
      transition: { duration: 0.1, ease: 'easeInOut' },
    });
  }, [isShow, controls]);

  return (
    <Portal renderTo={() => document.getElementById(SELECTOR.MAIN)}>
      <section className="sticky bottom-32 mt-30 flex w-full justify-center px-12">
        <section className="h-78 w-full">
          <motion.a
            animate={controls}
            className={cn(
              '[background-clip:content-box,border-box] [background-image:linear-gradient(#fff,#fff),linear-gradient(to_right,#0075ff_7%,#7f00ff_66.5%,#f577ca_100%)] [background-origin:border-box] [border:1.5px_solid_transparent] [box-shadow:0px_10px_15px_0px_#0000000f]',
              'mx-auto flex w-fit items-center overflow-hidden rounded-pill',
              {
                '!rounded-small w-full': isGrowUp,
              },
            )}
            href={ROUTES.RECOMMEND_POLICY_SITUATION_EMERGENCY_FORM.pathname}
            initial={{ opacity: 0, y: 10 }}
          >
            <motion.article
              animate={{
                height: isGrowUp ? 76 : 40,
              }}
              className={cn('flex w-fit items-center px-20', {
                '!w-full': isGrowUp,
              })}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
            >
              <AnimatePresence mode="wait">
                {!isGrowUp ? (
                  <section className="flex items-center gap-4">
                    <Image
                      alt="다음 콘텐츠 이어보기 : 소아응급 병원"
                      height={24}
                      src="/assets/pngs/recommend-policy/running.png"
                      width={24}
                    />
                    <p className="text-nowrap font-700 text-Gray-900 typo-body4">
                      다음 콘텐츠 이어보기 : 소아응급 병원
                    </p>
                    <SvgChevronright className="size-14 text-Gray-900" />
                  </section>
                ) : (
                  <section className="flex w-full items-center justify-between">
                    <section className="flex items-center gap-12">
                      <Image
                        alt="다음 콘텐츠 이어보기 : 소아응급 병원"
                        height={36}
                        src="/assets/pngs/recommend-policy/ambulance-org.png"
                        width={36}
                      />
                      <section className="flex flex-col">
                        <p className="text-nowrap font-600 text-Gray-700 typo-caption2">
                          밤늦게 갑자기 아이가 아픈 경험 다들 있으시죠?
                        </p>
                        <p className="text-nowrap font-600 text-Gray-900 typo-body4">
                          우리집에서 가장 가까운 소아 응급병원은?
                        </p>
                      </section>
                    </section>

                    <SvgChevronright className="size-14 text-Gray-900" />
                  </section>
                )}
              </AnimatePresence>
            </motion.article>
          </motion.a>
        </section>
      </section>
    </Portal>
  );
};
